import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { IconHeartFilled, IconHome, IconImport, IconLibrary, IconSearch } from "@/components/ui/icons";

const KEY = "sl.sidebar";

/**
 * Desktop sidebar with a Spotify-style collapse toggle.
 * Collapsed = 72px, icons only, tooltips on hover.
 * Expanded = 260px, icon + label.
 *
 * Width flips by setting a `data-sidebar` attribute on <body>; opus.css
 * overrides --sidebar-w on that selector, so the grid template updates
 * automatically for the whole shell (no width prop plumbing needed).
 */
export function Sidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
    document.body.dataset.sidebar = collapsed ? "collapsed" : "expanded";
    return () => { delete document.body.dataset.sidebar; };
  }, [collapsed]);

  return (
    <aside style={{
      height: "100%", background: "var(--bg-elev)",
      padding: collapsed ? "18px 8px" : "18px 12px",
      borderRight: "1px solid var(--line)",
      display: "flex", flexDirection: "column", gap: 4,
    }}>
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: collapsed ? "center" : "space-between",
        gap: 10, padding: collapsed ? "6px 0 16px" : "6px 10px 16px",
      }}>
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <img src="/favicon-v2.svg" width={26} height={26} alt="sonslow"/>
            <strong className="trunc">sonslow</strong>
          </div>
        )}
        <button
          data-sfx="tap"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? "Ouvrir la barre latérale" : "Réduire la barre latérale"}
          aria-label={collapsed ? "Ouvrir la barre latérale" : "Réduire la barre latérale"}
          style={{
            padding: 6, borderRadius: 8, color: "var(--fg-mute)",
            flexShrink: 0, fontSize: 18, lineHeight: 1,
            width: 28, height: 28,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {collapsed ? "›" : "‹"}
        </button>
      </div>
      <SidebarLink collapsed={collapsed} to="/"        icon={<IconHome />}    label="Accueil" />
      <SidebarLink collapsed={collapsed} to="/search"  icon={<IconSearch />}  label="Rechercher" />
      <SidebarLink collapsed={collapsed} to="/library" icon={<IconLibrary />} label="Bibliothèque" />
      <SidebarLink collapsed={collapsed} to="/liked"   icon={<IconHeartFilled />} label="Titres likés" />
      <SidebarLink collapsed={collapsed} to="/import"  icon={<IconImport />}  label="Importer" />
    </aside>
  );
}

function SidebarLink({ to, icon, label, collapsed }: {
  to: string; icon: React.ReactNode; label: string; collapsed: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-sfx="tap"
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        display: "flex", alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        padding: collapsed ? "10px 0" : "10px 12px",
        borderRadius: 8,
        color: isActive ? "var(--fg)" : "var(--fg-mute)",
        background: isActive ? "var(--accent-soft)" : "transparent",
      })}
    >
      {icon}
      {!collapsed && <span style={{ marginLeft: 12 }}>{label}</span>}
    </NavLink>
  );
}
